import { useId, useMemo } from 'react';
import { useGamePassport } from '../lib/useGamePassport';
import { fixtureTimeLabel } from '../lib/matchday';
import { matchLink } from '../lib/share';
import type { Fixture, FixtureSource, PredictionPick } from '../lib/types';
import { ShareButton } from './ShareButton';
import './call-receipt.css';

const PICK_LABEL: Record<PredictionPick, string> = { W: 'Win', D: 'Draw', L: 'Loss' };

function safeSource(value: string | null): string | null {
  try { return value && new URL(value).protocol === 'https:' ? value : null; } catch { return null; }
}

function checkedLabel(value: string | null): string | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A device's saved call meets the already shared, confirmed result feed. */
export function CallReceipt({ fixture: inputFixture, season: inputSeason, source, stale = false }: { fixture: Fixture; season: string; source?: FixtureSource; stale?: boolean }) {
  const titleId = useId();
  const fixtures = useMemo(() => [inputFixture], [inputFixture]);
  const { tickets, storageAvailable } = useGamePassport(fixtures, inputSeason, source, stale, false);
  const ticket = tickets.find((item) => item.fixture.id === inputFixture.id);
  if (!ticket?.call) return null;
  const { fixture, season } = ticket;
  const settled = ticket.outcome !== null && ticket.score !== null;
  const correct = settled && ticket.call === ticket.outcome;
  const score = ticket.score;
  const resultLabel = settled && score ? `Arsenal ${score.ours}–${score.theirs} ${fixture.opponent}` : null;
  const sourceUrl = safeSource(ticket.sourceUrl);
  const provider = ticket.sourceProvider === 'openligadb' ? 'OpenLigaDB' : ticket.sourceProvider;
  const licenseUrl = safeSource(ticket.licenseUrl);
  const licenseLabel = ticket.licenseLabel ?? (licenseUrl ? 'Data licence' : null);
  const attribution = [provider, licenseLabel].filter(Boolean).join(' · ');
  const checked = checkedLabel(ticket.checkedAt);
  const pending = fixture.status === 'postponed'
    ? 'Postponed. Your call stays with this fixture while it waits for a new date.'
    : fixture.timeTbc
      ? 'Kick-off TBC. Your call waits for a confirmed final result.'
      : stale
        ? 'Waiting for a fresh confirmed result. Saved or unfinished scores do not settle this receipt.'
        : 'Waiting for a confirmed final result. This receipt updates automatically with the matchbook.';

  return (
    <section className={`call-receipt${settled ? ' call-receipt--settled' : ''}`} aria-labelledby={titleId}>
      <div className="call-receipt__top"><span>CALL YOUR SHOT · {season}</span><span className="call-receipt__stamp">{settled ? correct ? 'CALLED IT' : 'NOT THIS TIME' : 'ON THE RECORD'}</span></div>
      <h3 id={titleId}>{settled ? correct ? 'You called it.' : 'That’s football. Next one.' : 'Your call is in.'}</h3>
      <p className="call-receipt__match">{resultLabel ?? `Arsenal ${fixture.venue === 'A' ? 'at' : 'v'} ${fixture.opponent}`}</p>
      <p className="call-receipt__meta">{fixture.comp} · {fixtureTimeLabel(fixture)}</p>
      <dl className="call-receipt__facts">
        <div><dt>Your call</dt><dd>{PICK_LABEL[ticket.call]}</dd></div>
        <div><dt>Result</dt><dd>{settled && ticket.outcome ? PICK_LABEL[ticket.outcome] : 'Awaiting confirmation'}</dd></div>
      </dl>
      {!settled && <p className="call-receipt__pending" role="status">{pending}</p>}
      <div className="call-receipt__foot">
        <p>Personal record on this device.{!storageAvailable ? ' Device storage is unavailable; this receipt may not survive a reload.' : ' It is not an independently certified pre-match record.'}</p>
        {settled && <p>{sourceUrl ? <a href={sourceUrl} target="_blank" rel="noreferrer">{provider ? `${provider} result source` : 'Result source'}</a> : provider ? `${provider} result` : 'Confirmed matchbook result'}{licenseUrl ? <> · <a href={licenseUrl} target="_blank" rel="noreferrer">{licenseLabel}</a></> : licenseLabel ? ` · ${licenseLabel}` : ''}{checked ? ` · checked ${checked}` : ''}</p>}
      </div>
      {settled && resultLabel && <ShareButton ghost label="Share my result receipt" moment={{
        title: 'COYG — my result receipt',
        text: `${resultLabel}. My call: ${PICK_LABEL[ticket.call]} — ${correct ? 'called it!' : 'not this time.'} Personal record from my COYG device.${attribution ? ` Result data: ${attribution}${licenseUrl ? ` ${licenseUrl}` : ''}.` : ''}`,
        url: matchLink(fixture.id),
        card: { eyebrow: `MY ${season} RESULT RECEIPT`, headline: correct ? 'Called it.' : 'Next one.', detail: `${resultLabel} · My call: ${PICK_LABEL[ticket.call]}${attribution ? ` · ${attribution}` : ''}`, stamp: 'MY DEVICE RECORD · COYG.ORG', personal: true },
      }}/>}
    </section>
  );
}
